import { Component, Input, OnInit } from '@angular/core';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { BehaviorSubject } from 'rxjs';
import { SelectedCardDetails, SharedService } from '../../core/services/shared.service';
import { NgxTippyService } from 'ngx-tippy-wrapper';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import _ from 'lodash';
import { CalendarService } from '../services/calendar.service';

@Component({
  selector: 'app-calendar-settings-card',
  templateUrl: './calendar-settings-card.component.html',
  styleUrls: ['./calendar-settings-card.component.scss']
})
export class CalendarSettingsCardComponent implements OnInit {
  @Input() module: string;
  @Input() options: any;
  @Input() sectionType: string;

  maxRow = 5;
  maxColumn = 2;
  selectedPosition: any;
  customSelectedAttributes: any = [];
  attributeList: any;
  cardPreviewHTML: string = '';
  optionsAvailable: BehaviorSubject<any>;
  optionsBucket: any[] = [];
  badgeLabels = ['Skillsets'];
  singletonRows = ['work_order_scheduled_date@Scheduled-Date-&-Time'];
  moduleFormMap = {
    'CALENDAR': this._sharedService.calendarForm
  };
  moduleAttributeMap = {
    'weekday':  this._sharedService.jobAttributes,
    'month': this._sharedService.jobMonthAttributes
  };
  tippyProps = {
    allowHTML: true,
    theme: 'light',
    trigger: 'click',
    placement: 'right',
    interactive: true,
    appendTo: document.body
  };
  cardViewType = {
    'weekday': 'card_view',
    'month': 'month_card_view'
  };


  objectKeys = Object.keys;

  constructor(
    private _localisationService: LocalisationService,
    public _sharedService: SharedService,
    public tippyService: NgxTippyService,
    private _utilityService: UtilityService,
    private calendarService: CalendarService
  ) { }


  ngOnInit(): void {
    this._localisationService.setDefaultLanguage();
    this.attributeList = this.moduleAttributeMap[this.sectionType];
    this.optionsAvailable = new BehaviorSubject<any>(this.attributeList);
    this.setSelectionLimiters();
    this.initiateSelectedCardDetails(this.module);
    this.initiateAvailableOptions();
  }

  initiateAvailableOptions(): void {
    Object.keys(this.customSelectedAttributes).forEach((row) => {
      this.customSelectedAttributes[row].forEach((option) => {
        this.deleteAvailableOptions(option);
      });
    });
  }

  setSelectionLimiters(): void {
    if (!this._utilityService.isEmptyOrUndefined(this.options)) {
      this.maxColumn = this.options?.MAX_COLUMN;
      this.maxRow = this.options?.MAX_ROW;
    }
  }

  initiateSelectedCardDetails(module: string): void {
    const lastSelectedDetails = this.moduleFormMap[module].get(this.cardViewType[this.sectionType]).value || [];
    let filteredList = {};
    let newRowIndex = 1;
    Object.keys(lastSelectedDetails).map(key => Number(key)).forEach((rowNumber) => {
      filteredList[newRowIndex] = [];
      lastSelectedDetails[rowNumber].forEach((attribute: any, columnIndex: number) => {
        if (_.some(this.attributeList, { key: attribute.key })) {
          attribute.column = columnIndex + 1;
          attribute.row = newRowIndex;
          attribute = this.calendarService.setDisplayKeyAndValue(attribute, this.attributeList);
          filteredList[newRowIndex].push(attribute);
        }
      });
      if (filteredList[newRowIndex].length === 0) {
        delete filteredList[newRowIndex];
      }
      else {
        newRowIndex ++;
      }
    });
    
    if (this.module === 'CALENDAR' && this.sectionType === 'ROUTE') {
      filteredList =  this.convertSelectedAttributesToCustomColumns(filteredList);
    }
    this.customSelectedAttributes = filteredList;
  }

  groupByKey(data, key): void {
    return data.reduce((ele, x) => {
      (ele[x[key]] = ele[x[key]] || []).push(x);
      return ele;
    }, {});
  };

  sortByKey(array, key): void {
    return array.sort((a, b) => {
      const x = a[key]; const y = b[key];
      return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
  }

  counter(i: number): any {
    return Array(i).fill(1).map((x, i) => i + 1);
  }

  onTippyOpen(event: any, isNewRow: boolean): void {
    this._sharedService.refreshAddNewAttributeRow = isNewRow;
    this.selectedPosition = event.srcElement.data;
    this._sharedService.tippyName = event.srcElement.data.tippyName;
  }

  updateSelectedAttribute(option: any, action: string): void {
    const row = option.row;
    switch (action) {
      case 'Add': {
        if (this.customSelectedAttributes.hasOwnProperty(row)) {
          this.customSelectedAttributes[row].push(option);
        }
        else {
          this.customSelectedAttributes[row] = [{ ...option }];
        }
        break;
      }
      case 'Delete': {
        _.remove(this.customSelectedAttributes[row], { key: option.key, label: option.label });
        this.sanitizeSelectedAttribute(option);
        break;
      }
    }
    this._sharedService.updateFormFields(this.moduleFormMap[this.module], this.cardViewType[this.sectionType], this.customSelectedAttributes);
  }


  sanitizeSelectedAttribute(deletedValue: any): void {
    if (this.customSelectedAttributes[deletedValue.row].length === 0) {
      delete this.customSelectedAttributes[deletedValue.row];
      const selectedAttributes = {};
      let rowCount = 1;
      Object.keys(this.customSelectedAttributes).map(key => Number(key)).forEach((rowNumber: any) => {
        selectedAttributes[rowCount] = this.customSelectedAttributes[rowNumber].map((ele: any) => ({ ...ele, row: rowCount }));
        rowCount++;
      });
      this.customSelectedAttributes = selectedAttributes;
    }
  }

  onAttributeSelected(selectedValue: any): void {
    const selectedOption = { ...selectedValue, ...this.selectedPosition };
    this.deleteAvailableOptions(selectedValue);
    this.updateSelectedAttribute(selectedOption, 'Add');
  }


  onAttributeDeleted(deletedValue: SelectedCardDetails): void {
    if (deletedValue.dismissable) {
      this.updateSelectedAttribute(deletedValue, 'Delete');
      this.addAvailableOption(deletedValue);
    }
  }

  getAvailableOptions(): any {
    return this.optionsAvailable;
  }

  updateAvailableOptions(options: any): void {
    this.optionsAvailable.next(options);
  }

  addAvailableOption(option: any): void {
    this.removeFromOptionsBucket(option);
    const availableOptions = [...this.optionsAvailable.value];
    availableOptions.push(option);
    const withId = availableOptions.filter(obj => obj.hasOwnProperty('id'));
    const withoutId = availableOptions.filter(obj => !obj.hasOwnProperty('id'));
    const sortedWithId = withId.sort((a, b) => a.id - b.id);
    const sortedArray = [...sortedWithId, ...withoutId];
    this.updateAvailableOptions(sortedArray);

  }

  deleteAvailableOptions(deletedOption: any): void {
    let availableOptions = [...this.optionsAvailable.value];
    availableOptions = availableOptions.filter(option => option.key !== deletedOption.key);
    this.updateAvailableOptions(availableOptions);
    this.addToOptionsBucket(deletedOption);
  }


  addToOptionsBucket(data: any): void {
    this.optionsBucket.push(data);
  }

  removeFromOptionsBucket(deletedOption: any): void {
    this.optionsBucket = this.optionsBucket.filter(option => option.key !== deletedOption.key);
  }

  convertSelectedAttributesToCustomColumns(selectionObject: any, newColumnCount: number = this.maxColumn): any {
    const allAttributes = Object.values(selectionObject).flat();
    const updatedSelectionObject = {};
    let totalAttributes = allAttributes.length - 1;
    let row = 1; let column = 1; let iterIndex = 0;
    while (totalAttributes >= 0) {
      let attribute: any = allAttributes[iterIndex];
      if (this.singletonRows.includes(attribute?.key)) {
        row = Object.keys(updatedSelectionObject).length + 1;
        column = 1;
        attribute.row = row;
        attribute.column = column;
        attribute = this.calendarService.setDisplayKeyAndValue(attribute, this.attributeList);
        updatedSelectionObject[row] = [attribute];
        totalAttributes --;
        iterIndex ++;
        row ++;
        continue;
      }
      if (column > newColumnCount) {
        column = 1;
        row ++;
      }
      attribute.row = row;
      attribute.column = column;
      attribute = this.calendarService.setDisplayKeyAndValue(attribute, this.attributeList);
      if(updatedSelectionObject.hasOwnProperty(row)) {
        updatedSelectionObject[row].push(attribute);
      }
      else {
        updatedSelectionObject[row] = [attribute];
      }
      totalAttributes --;
      iterIndex ++;
      column ++;
    }
    return updatedSelectionObject;
  }
}
